<template>
    <section class="card-list-template" :style="getStyle">
        <CommonTitleTemplate :title="title" @goMore="moreHandle"></CommonTitleTemplate>
        <div class="card-list-wrap" v-if="showList?.length">
            <div
                v-for="(item, index) of showList"
                :key="index"
                :style="itemStyle"
                :class="[
                    'card-item',
                    {
                        'card-item-img-top': imgPos === 'top',
                        'card-item-img-left': imgPos === 'left',
                        'card-item-img-right': imgPos === 'right',
                        'card-item-img-bottom': imgPos === 'bottom',
                        'card-item-img-full': imgPos === 'full',
                    },
                ]"
                @click="goDetail(item)"
            >
                <div class="card-item-img">
                    <LazyImage :src="item.projectLogo" className="img" />
                </div>
                <div class="card-item-content">
                    <div class="title">
                        <Ellipsis :rows="2" :content="item.projectName ?? '-'" />
                    </div>
                    <div>
                        <div class="extension-info">阶段：{{ item.stageJson?.name ?? '-' }}</div>
                        <div class="extension-info">年份：{{ item.projectYear ?? '-' }}</div>
                    </div>
                </div>
                <div
                    :class="['status-tag', { 'status-inprogress': item.stateName?.includes('中') }]"
                >
                    {{ item.stateName }}
                </div>
            </div>
        </div>
        <BigNoData v-else class="no-data" url="no-lesson.png" :info="`暂无数据`" />
        <CodeDialog
            @on-close="onClose"
            @on-confirm="onConfirm"
            :visible="codeVisible"
            :project-detail="activeItem"
        />
    </section>
</template>

<script setup lang="ts">
import CommonTitleTemplate from './common-title-template.vue';
import Ellipsis from '@/components/ellipsis.vue';
import BigNoData from '@/components/no-data.vue';
import LazyImage from '@/components/lazy-image/lazy-image.vue';
import CodeDialog from '@/views/shenzhen/components/code-dialog.vue';
import { computed, onMounted, PropType, reactive, ref } from 'vue';
import { isObject, isString, result } from 'lodash';
import { pageJump } from '@/global/location-transfer';
import { jumpHref } from '@/router/jumpHref';
import { ElButton } from 'element-plus';

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    imgPos: {
        type: String as PropType<'top' | 'left' | 'right' | 'bottom' | 'full'>,
        default: 'top',
    },
    imgHeight: {
        type: String,
        default: '152px',
    },
    imgWidth: {
        type: String,
        default: '285px',
    },
    request: {
        type: [String, Object] as PropType<string | { name: string; resultKey: string }>,
        default: '',
    },
    requestParams: {
        type: Object as PropType<Record<string, any>>,
        default: () => ({}),
    },
    list: {
        type: Array as PropType<any[]>,
        default: () => [],
    },
    columnsNum: {
        type: Number,
        default: 2,
    },
    border: {
        type: Boolean,
        default: true,
    },
});

const showList = ref<any[]>([]);
const codeVisible = ref(false);
const activeItem = ref<any>({});

const filterData = reactive({
    pageIndex: 1,
    pageSize: 4,
});

const getStyle = computed(() => {
    return `
        --img-height: ${props.imgHeight};
        --img-width: ${props.imgWidth};
        --content-width: ${['top', 'bottom'].includes(props.imgPos) ? props.imgWidth : 'auto'};
        --columns-num: ${props.columnsNum};
        --item-border: ${props.border ? '1px solid #eaeaea' : 'none'};
        `;
});

function getValueByPath(obj: any, path: string) {
    const keys = path.split('.');
    if (!keys.length) {
        return;
    }
    if (keys.length === 1) {
        return obj[path];
    }
    const value = keys.reduce((prev, current) => {
        return prev?.[current];
    }, obj);

    return value;
}

function getList() {
    const { requestParams = {}, request } = props;
    if (!request) {
        showList.value = props.list;
        return;
    }
    const requestName = isString(request) ? request : request.name;
    G_transmit(requestName, {
        params: {
            ...(requestParams.filter?.type === 'query' ? filterData : {}),
            ...(requestParams.params ?? {}),
        },
        data: {
            ...(requestParams.filter?.type === 'body' ? filterData : {}),
            ...(requestParams.data ?? {}),
        },
        message: false,
    }).then(res => {
        if (isObject(request)) {
            showList.value = getValueByPath(res, request.resultKey) ?? [];
        } else if (res.data && res.data.rows) {
            showList.value = res.data.rows || [];
        }
        console.log('showList: ', showList);
    });
}

function goDetail(item) {
    activeItem.value = item;
    if (item?.eventsId) {
        // 有赛事id则判断是否需要邀请码
        G_transmit('isSetInvite', {
            name: 'isSetInvite',
            queries: {
                projectId: item.id,
            },
        }).then(res => {
            if (res.data === true) {
                codeVisible.value = true;
            } else {
                // 不需要进行跳转
                if (item?.stageJson?.code >= 50) {
                    pageJump(
                        {
                            name: 'scientificResearchProjectGroup',
                            query: { id: item?.id, name: item?.projectName },
                        },
                        true,
                    );
                } else {
                    pageJump(
                        jumpHref.rating2.matchRatingDetail(item?.eventsId, {
                            projectType: item?.projectTypeJson?.code,
                        }),
                        true,
                    );
                }
            }
        });
    } else {
        G_dispatchEvent('toast', { message: '申报暂未开始', type: 'info' });
    }
}

function onClose() {
    codeVisible.value = false;
}

// 验证通过后
function onConfirm() {
    onClose();
    if (activeItem.value?.stageJson?.code >= 50) {
        pageJump(
            {
                name: 'scientificResearchProjectGroup',
                query: { id: activeItem.value?.id, name: activeItem.value?.projectName },
            },
            true,
        );
    } else {
        pageJump(
            jumpHref.rating2.matchRatingDetail(activeItem.value?.eventsId, {
                projectType: activeItem.value?.projectTypeJson?.code,
            }),
            true,
        );
    }
}

function moreHandle() {
    pageJump({ name: 'scientificResearchProjectCenter' }, true);
}

onMounted(() => {
    getList();
});
</script>

<style lang="scss" scoped>
.card-list-template {
    .card-list-wrap {
        display: grid;
        grid-template-columns: repeat(var(--columns-num), 1fr);
        gap: 20px;
        margin-top: 26px;
    }

    .card-item {
        position: relative;
        width: fit-content;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        flex-direction: column;

        &-img-left {
            flex-direction: row;
        }

        &-img-right {
            flex-direction: row-reverse;
        }

        &-img-bottom {
            flex-direction: column-reverse;
        }

        &-img-full {
            position: relative;
            min-height: var(--img-height);

            .card-item-img {
                position: absolute;
            }

            .card-item-content {
                position: relative;
                z-index: 1;
                color: #fff;
            }
        }

        &-img-left,
        &-img-right {
            .card-item-content {
                height: 100%;
            }

            .card-item-img {
                img {
                    height: 100%;
                }
            }
        }

        &:hover {
            box-shadow: 0px 1px 12px 0px rgba(0, 0, 0, 0.15);

            .title {
                color: #2164c9;
            }
        }
    }

    .card-item-img {
        flex-shrink: 0;
        width: var(--img-width);
        height: var(--img-height);
        overflow: hidden;
    }

    .card-item-content {
        width: var(--content-width);
        height: 132px;
        padding: 12px 20px 20px;
        border: var(--item-border);
    }

    .title {
        height: 48px;
        line-height: 24px;
        font-weight: bold;
        font-size: 16px;
        color: #000000;
    }

    .extension-info {
        margin-top: 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #666666;
        font-size: 14px;
        line-height: 14px;
    }

    .status-tag {
        position: absolute;
        top: -4px;
        left: 10px;
        z-index: 2;
        width: 54px;
        height: 26px;
        line-height: 26px;
        color: #ffffff;
        text-align: center;
        background: linear-gradient(180deg, #ffbb66 0%, #f1660a 100%);
        border-radius: 0px 0px 0px 10px;

        &::after {
            position: absolute;
            top: 0;
            right: -4px;
            z-index: 1;
            content: '';
            width: 4px;
            height: 5px;
            background: linear-gradient(to bottom left, transparent 50%, #c20e01 50%);
        }

        &.status-inprogress {
            background: linear-gradient(180deg, #86b0ef 0%, #3775d1 100%);

            &::after {
                background: linear-gradient(to bottom left, transparent 50%, #306eca 50%);
            }
        }
    }
}
</style>
